/**
 * ActionPlanModal — supplement recommendations + dose-calculator link.
 *
 * Verbatim port of the standalone's inline ActionPlanModal with Bonus B
 * integration (Open in Dose Calculator → button on each supplement card).
 */
import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { classifyResult, SUPPLEMENT_RECOMMENDATIONS, stockSolutionKeyForParameter, getIdealRangeForParameter } from '@reefapp/shared';
import type { TestLogEntry, Supplement, SupplementParameter } from '@reefapp/shared';
import { getPreferredProductForParameter } from '../manual-readings/preferredProducts';
import { COMMERCIAL_PRODUCTS_CATALOG } from '@reefapp/shared';

export interface ActionPlanModalProps {
  log: TestLogEntry;
  onClose(): void;
}

export function ActionPlanModal({ log, onClose }: ActionPlanModalProps) {
  const navigate = useNavigate();
  const status = classifyResult(log.parameterKey, log.result);

  const statusLabel: string =
    status === 'low' ? 'Low' : status === 'high' ? 'High' : status === 'ideal' ? 'Ideal' : 'N/A';
  const statusColor: string =
    status === 'low'
      ? 'bg-yellow-500'
      : status === 'high'
        ? 'bg-red-500'
        : status === 'ideal'
          ? 'bg-green-500'
          : 'bg-slate-400';

  const relevantSupplements: ReadonlyArray<Supplement> = useMemo(() => {
    const paramLower = log.parameter.toLowerCase();
    let paramKey: SupplementParameter = 'Other';
    if (paramLower.includes('alkalinity') || paramLower.includes('kh'))
      paramKey = 'Alkalinity';
    else if (paramLower.includes('calcium')) paramKey = 'Calcium';
    else if (paramLower.includes('magnesium')) paramKey = 'Magnesium';
    else if (paramLower.includes('nitrate')) paramKey = 'Nitrate';
    else if (paramLower.includes('phosphate')) paramKey = 'Phosphate';

    return SUPPLEMENT_RECOMMENDATIONS.filter(
      (sup) => sup.parameter === paramKey || sup.parameter === 'All',
    );
  }, [log.parameter]);

  const solutionKey = stockSolutionKeyForParameter(log.parameterKey);
  const idealRange = getIdealRangeForParameter(log.parameterKey);
  const canDeepLink =
    solutionKey !== null &&
    idealRange !== null &&
    typeof log.result === 'number' &&
    status !== 'ideal' &&
    status !== 'unknown';

  const handleDiyDoseLink = () => {
    if (!canDeepLink || typeof log.result !== 'number') return;
    const idealMid = (idealRange!.min + idealRange!.max) / 2;
    const params = new URLSearchParams({
      solution: solutionKey!,
      current: String(log.result),
      target: String(idealMid),
      unit: log.unit,
    });
    navigate(`/stock-solutions?${params.toString()}`);
  };

  // Phase 4: "Dose to Fix" with commercial product deep-link
  const preferredProductId = getPreferredProductForParameter(log.parameter);
  const preferredProduct = preferredProductId
    ? COMMERCIAL_PRODUCTS_CATALOG.find((p) => p.id === preferredProductId) ?? null
    : null;

  const canDoseCommercial =
    typeof log.result === 'number' &&
    status !== 'ideal' &&
    status !== 'unknown' &&
    idealRange !== null &&
    preferredProduct !== null;

  const handleCommercialDoseLink = useCallback(() => {
    if (!canDoseCommercial || typeof log.result !== 'number') return;
    const idealMid = (idealRange!.min + idealRange!.max) / 2;
    const delta =
      status === 'low' ? idealMid - log.result : log.result - idealMid;
    const params = new URLSearchParams({
      product: preferredProduct!.id,
      delta: String(Math.abs(delta)),
      unit: log.unit,
    });
    navigate(`/stock-solutions?${params.toString()}`);
  }, [canDoseCommercial, log, status, idealRange, preferredProduct, navigate]);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-slate-800 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between p-5 border-b border-slate-700">
          <div>
            <h3 className="text-2xl font-bold text-slate-100">
              Action Plan for {log.parameter}
            </h3>
            <p className="text-slate-400">
              Result: {String(log.result)} {log.unit}
            </p>
          </div>
          <button
            className="p-1 text-slate-400 hover:text-slate-200 text-3xl font-semibold"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="p-6 flex-auto overflow-y-auto space-y-4">
          <div
            className={`p-4 rounded-md text-white font-bold ${statusColor}`}
          >
            Current status is {statusLabel}.
          </div>
          {status !== 'ideal' && status !== 'unknown' && (
            <div>
              <h4 className="text-lg font-semibold text-slate-200 mb-2">
                Recommended Actions:
              </h4>
              <ul className="list-disc pl-5 space-y-2 text-slate-400">
                {status === 'high' && (
                  <li>
                    Consider performing a water change to lower the parameter.
                  </li>
                )}
                {status === 'low' && (
                  <li>
                    Consider dosing a supplement to raise the parameter.
                  </li>
                )}
                <li>
                  Investigate the root cause (e.g., overfeeding, equipment
                  issues, coral consumption).
                </li>
                <li>
                  Make changes slowly and continue to test regularly to monitor
                  the effects of your actions.
                </li>
              </ul>
            </div>
          )}
          {canDoseCommercial && (
            <button
              onClick={handleCommercialDoseLink}
              className="w-full px-4 py-2 rounded-md text-sm font-semibold bg-amber-600 text-white hover:bg-amber-500 transition-colors"
            >
              Dose to Fix with {preferredProduct?.name ?? 'Product'} →
            </button>
          )}
          {canDeepLink && (
            <button
              onClick={handleDiyDoseLink}
              className="w-full px-4 py-2 rounded-md text-sm font-semibold bg-cyan-600 text-white hover:bg-cyan-500 transition-colors"
            >
              Open in DIY Dose Calculator →
            </button>
          )}
          <div>
            <h4 className="text-lg font-semibold text-slate-200 mb-2">
              Relevant Supplements:
            </h4>
            {relevantSupplements.length > 0 ? (
              <div className="space-y-3">
                {relevantSupplements.map((sup) => (
                  <div
                    key={sup.slug}
                    className="p-3 bg-slate-700/50 border border-slate-600 rounded-md"
                  >
                    <p className="font-bold text-slate-200">
                      {sup.name}{' '}
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          sup.type === 'DIY'
                            ? 'bg-orange-500/20 text-orange-300'
                            : 'bg-blue-500/20 text-blue-300'
                        }`}
                      >
                        {sup.brandOrType}
                      </span>
                    </p>
                    <p className="text-sm text-slate-400 mt-1">
                      {sup.description}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500">
                No specific supplements found for this parameter.
              </p>
            )}
          </div>
        </div>
        <footer className="flex items-center justify-end p-4 border-t border-slate-700">
          <button
            onClick={onClose}
            className="bg-slate-600 text-white font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none transition-all duration-150"
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}
